<template>
  <div
    class="mx-auto flex min-h-[calc(100dvh-64px)] max-w-4xl flex-col pb-2 md:h-[calc(100dvh-80px)] md:min-h-auto md:pb-10"
  >
    <div class="flex grow flex-col justify-center">
      <h3
        class="text-headline-small md:text-display-small lg:text-display-medium mb-10 text-center"
      >
        {{
          isSelfFeedback
            ? $t('common.selfFeedback')
            : $t('common.feedbackTo') + ' ' + receiver.firstName
        }}
      </h3>
      <div class="flex w-full flex-row items-center justify-center px-2">
        <FinishFeedbackProgress
          :number-of-feedbacks="numberOfFeedbacks"
          :number-of-questions="numberOfQuestions"
          :percentage="percentage"
          show-background-shape
        />
        <FeedbackFlow class="h-10 w-20" />
        <BaseShapeMask shape="9-sided-cookie" animation="spin" :scale="1.1">
          <BaseMemberAvatar :member="receiver" size="160" class="z-10" />
        </BaseShapeMask>
      </div>
      <FinishFeedbackQuestionList
        :questions="questionsWithFeedback"
        @navigate="navigateToQuestionById"
      />
    </div>

    <FeedbackVisibilityInformation
      :receiver="receiver"
      :sharedFeedback="sharedFeedback"
      :supervisors="supervisors"
    />
    <div class="flex w-full flex-row items-end justify-between px-2">
      <BaseTextButton
        icon="keyboard_arrow_left"
        :size="{ base: 'S', sm: 'M' }"
        color="text"
        @click="goBackToFeedback"
        >{{ $t('common.back') }}</BaseTextButton
      >

      <BaseTextButton
        :size="{ base: 'S', sm: 'M' }"
        color="filled"
        icon="send"
        @click="
          () => {
            save(redirectRoute)
          }
        "
        >{{ $t('common.feedback-done') }}</BaseTextButton
      >
    </div>
  </div>
</template>
<script lang="ts" setup>
  import { computed, toRef } from 'vue'
  import { injectStrict } from '../../../../core/utils/inject-strict'
  import { giveFeedbackKey } from '../../composables/useGiveStructuredFeedback'
  import { useFinishFeedbackSummary } from '../../composables/use-finish-feedback-summary'
  import type { QuestionGroup } from '../../models/question-group'
  import BaseTextButton from '../../../../core/components/base/button/text-button/BaseTextButton.vue'
  import type { Member } from '../../../../core/models/member/member'
  import FeedbackFlow from '../../../../core/components/feedback-flow/FeedbackFlow.vue'
  import BaseShapeMask from '../../../../core/components/base/shapes/BaseShapeMask.vue'
  import type { ShareType } from '../../../../core/models/share-types'
  import BaseMemberAvatar from '../../../../core/components/base/avatar/BaseMemberAvatar.vue'
  import { useUserStore } from '../../../../stores/user.store'
  import FeedbackVisibilityInformation from './FeedbackVisibilityInformation.vue'
  import FinishFeedbackProgress from './FinishFeedbackProgress.vue'
  import FinishFeedbackQuestionList from './FinishFeedbackQuestionList.vue'

  interface Props {
    questionGroups: ReadonlyArray<QuestionGroup>
    receiver: Member
    isSuperpower: boolean
    sharedFeedback?: {
      type: ShareType
      members: ReadonlyArray<Member>
    }
    supervisors?: ReadonlyArray<Member>
  }

  const props = defineProps<Props>()

  const { feedbacks, save, navigateToQuestionById } = injectStrict(giveFeedbackKey)

  const {
    numberOfQuestions,
    numberOfFeedbacks,
    percentage,
    questionsWithFeedback,
    lastQuestion,
  } = useFinishFeedbackSummary(toRef(props, 'questionGroups'), feedbacks)

  const goBackToFeedback = () => {
    navigateToQuestionById(lastQuestion.value)
  }

  const userStore = useUserStore()
  const isSelfFeedback = computed(() => props.receiver.id == userStore.user.id)

  const redirectRoute = computed(() => {
    if (props.isSuperpower) {
      return { name: 'superpower' }
    }
    return undefined
  })
</script>
